import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
} from "../../../utils/permissionUtils";
import { organizationHasIsfModule } from "../../../utils/orgModuleAccess";
import EisfDocumentTypes from "../isf/EisfDocumentTypes";
import EisfDocumentCategories from "../isf/EisfDocumentCategories";
import CapaActionPlanningEmailConfigEditor from "../capa/CapaActionPlanningEmailConfigEditor";
import {
  EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS,
  EISF_SIGNATURE_REQUEST_EMAIL_MERGE_TAGS,
} from "../isf/eisfSignatureRequestEmailTags";
import { fetchEisfConfig, saveEisfConfig } from "../isf/eisfConfigService";
import "./CapaConfigMaster.scss";

const CONFIG_SECTIONS = [
  {
    key: "documentTypes",
    label: "Document Type",
    subtitle: "eISF document type master values",
    icon: "fas fa-tag",
    permissionKeys: ["EISF_CONFIGURE"],
  },
  {
    key: "documentCategories",
    label: "Document Category",
    subtitle: "eISF document category master values",
    icon: "fas fa-folder-tree",
    permissionKeys: ["EISF_CONFIGURE"],
  },
  {
    key: "signatureRequestEmail",
    label: "Signature request email",
    subtitle: "Email sent when a placeholder document requires signature",
    icon: "fas fa-envelope",
    permissionKeys: ["EISF_CONFIGURE"],
  },
];

function sectionVisible(section, userPermissions, isSystemAdmin) {
  if (isSystemAdmin) return true;
  const keys = section.permissionKeys || ["EISF_CONFIGURE"];
  return keys.some((key) => hasPermission(userPermissions, key));
}

function snapshotOf(subject, body) {
  return JSON.stringify({
    subject: String(subject || "").trim(),
    body: String(body || "").trim(),
  });
}

const EisfConfigMaster = () => {
  const { userData, loading } = useUserData();
  const [searchParams] = useSearchParams();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [activeSection, setActiveSection] = useState("documentTypes");
  const [sigSubject, setSigSubject] = useState("");
  const [sigBody, setSigBody] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const isSystemAdmin = isSystemAdministratorRole(userData?.Role);

  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(userData?.Permission || "");

  const visibleSections = useMemo(
    () => CONFIG_SECTIONS.filter((section) => sectionVisible(section, userPermissions, isSystemAdmin)),
    [userPermissions, isSystemAdmin],
  );

  const canAccess = useMemo(() => {
    if (loading || !userData) return false;
    if (isSystemAdmin) return true;
    if (!organizationHasIsfModule(userData)) return false;
    return hasPermission(userPermissions, "EISF_CONFIGURE");
  }, [userData, loading, isSystemAdmin, userPermissions]);

  const hasUnsavedChanges = useMemo(
    () => savedSnapshot !== "" && snapshotOf(sigSubject, sigBody) !== savedSnapshot,
    [sigSubject, sigBody, savedSnapshot],
  );

  const applyEmailConfig = useCallback((cfg) => {
    const subject = String(
      cfg?.signatureRequestEmailSubjectDisplay ||
        cfg?.signatureRequestEmailSubjectTemplate ||
        cfg?.signatureRequestEmailSubjectDefault ||
        EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.subject,
    ).trim();
    const body = String(
      cfg?.signatureRequestEmailBodyDisplay ||
        cfg?.signatureRequestEmailBodyTemplate ||
        cfg?.signatureRequestEmailBodyDefault ||
        EISF_SIGNATURE_REQUEST_EMAIL_DEFAULTS.body,
    ).trim();
    setSigSubject(subject);
    setSigBody(body);
    setSavedSnapshot(snapshotOf(subject, body));
  }, []);

  const loadEmailConfig = useCallback(
    async ({ showBlockingLoader = false } = {}) => {
      if (!userData?.OrganizationId) return;
      if (showBlockingLoader) setEmailLoading(true);
      else setReloading(true);
      if (showBlockingLoader) showLoader("Loading eISF email templates...");
      try {
        const cfg = await fetchEisfConfig(userData);
        applyEmailConfig(cfg);
      } catch (_e) {
        showAlert({
          title: "eISF Config",
          message: "Unable to load signature request email templates.",
          type: "danger",
        });
      } finally {
        hideLoader();
        setEmailLoading(false);
        setReloading(false);
      }
    },
    [userData, applyEmailConfig, showLoader, hideLoader, showAlert],
  );

  useEffect(() => {
    const section = searchParams.get("section");
    if (section && visibleSections.some((item) => item.key === section)) {
      setActiveSection(section);
    }
  }, [searchParams, visibleSections]);

  useEffect(() => {
    if (
      visibleSections.length > 0 &&
      !visibleSections.some((section) => section.key === activeSection)
    ) {
      setActiveSection(visibleSections[0].key);
    }
  }, [visibleSections, activeSection]);

  useEffect(() => {
    if (!canAccess || !userData?.OrganizationId) return;
    if (activeSection !== "signatureRequestEmail") return;
    loadEmailConfig({ showBlockingLoader: true });
  }, [canAccess, userData?.OrganizationId, activeSection, loadEmailConfig]);

  const handleSaveEmail = async () => {
    if (!userData?.OrganizationId) return;
    const subject = String(sigSubject || "").trim();
    const body = String(sigBody || "").trim();
    if (!subject || !body) {
      showAlert({
        title: "eISF Config",
        message: "Subject and body are required for the signature request email.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    showLoader("Saving eISF email templates...");
    try {
      const result = await saveEisfConfig(
        {
          signatureRequestEmailSubjectTemplate: subject,
          signatureRequestEmailBodyTemplate: body,
        },
        userData,
      );
      if (!result.success) {
        showAlert({
          title: "eISF Config",
          message: result.message || "Could not save email templates.",
          type: "danger",
        });
        return;
      }
      setSavedSnapshot(snapshotOf(subject, body));
      showAlert({
        title: "eISF Config",
        message: result.message || "Signature request email templates saved.",
        type: "success",
      });
      await loadEmailConfig({ showBlockingLoader: false });
    } catch (_e) {
      showAlert({
        title: "eISF Config",
        message: "Could not save email templates.",
        type: "danger",
      });
    } finally {
      hideLoader();
      setSaving(false);
    }
  };

  if (loading || !userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  if (!canAccess) return <Navigate to="/notification-center" replace />;

  return (
    <div className="user-mgmt-page capa-config-saas">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "eISF Config" },
        ]}
      />

      <InfoAlert
        show={alertState.show}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        onClose={hideAlert}
      />

      <div className="row g-3 capa-config-saas__layout">
        <div className="col-md-4 col-xl-3 capa-config-saas__nav-col">
          <div className="card capa-config-saas__nav-card">
            <div className="card-header fw-semibold">Configuration sections</div>
            <div className="list-group list-group-flush capa-config-saas__nav-list">
              {visibleSections.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  className={`list-group-item list-group-item-action capa-config-saas__nav-item ${
                    activeSection === section.key ? "active" : ""
                  }`}
                  onClick={() => setActiveSection(section.key)}
                  aria-current={activeSection === section.key ? "page" : undefined}
                >
                  <div className="d-flex align-items-start gap-2">
                    <i className={`${section.icon} mt-1`} aria-hidden="true" />
                    <div>
                      <div className="fw-semibold capa-config-saas__nav-item-title">
                        {section.label}
                      </div>
                      <div
                        className={`small capa-config-saas__nav-item-subtitle ${
                          activeSection === section.key ? "text-white-50" : "text-muted"
                        }`}
                      >
                        {section.subtitle}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="col-md-8 col-xl-9 capa-config-saas__content-col">
          {activeSection === "documentTypes" ? (
            <div className="card shadow-sm capa-config-saas__panel-card">
              <div className="card-body">
                <EisfDocumentTypes embedded />
              </div>
            </div>
          ) : null}

          {activeSection === "documentCategories" ? (
            <div className="card shadow-sm capa-config-saas__panel-card">
              <div className="card-body">
                <EisfDocumentCategories embedded />
              </div>
            </div>
          ) : null}

          {activeSection === "signatureRequestEmail" ? (
            <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">
              <div className="card-body capa-ap-email-panel-body">
                {hasUnsavedChanges ? (
                  <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">
                    <i className="fas fa-exclamation-triangle me-2" />
                    You have unsaved changes to the signature request email.
                  </div>
                ) : null}

                <p className="text-muted small mb-3 flex-shrink-0">
                  Sent to staff whose roles match the placeholder signer roles on that study
                  site when a document is uploaded under a placeholder that requires signature.
                  Defaults are used until you save organisation-specific templates.
                </p>

                <h6 className="mb-2 flex-shrink-0">Signature request — email template</h6>

                <div className="capa-ap-email-editor-grow">
                  {emailLoading ? (
                    <div className="d-flex justify-content-center p-4">
                      <div className="spinner-border" role="status" />
                    </div>
                  ) : (
                    <CapaActionPlanningEmailConfigEditor
                      subjectTemplate={sigSubject}
                      onSubjectChange={setSigSubject}
                      bodyTemplate={sigBody}
                      onBodyChange={setSigBody}
                      mergeTags={EISF_SIGNATURE_REQUEST_EMAIL_MERGE_TAGS}
                      fillAvailableHeight
                    />
                  )}
                </div>

                <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0 gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveEmail}
                    disabled={saving || reloading || emailLoading}
                  >
                    {saving || reloading
                      ? saving
                        ? "Saving..."
                        : "Loading..."
                      : "Save email templates"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EisfConfigMaster;
